import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import t from './tokens';
import TemplatePreview from './TemplatePreview';

// Full-page package builder, styled to the Shoob v2 Figma "Build your package"
// screen: a large sheet preview on the left, pose + background carousels on the
// right (with "apply to all"), per-sheet navigation, and a sticky add-to-cart
// bar. Each print slot expands into one customizable sheet (image_type ×
// sheet_count); the gallery-selected pose pre-fills every sheet so a many-sheet
// package is still one tap, while any sheet can be personalized. Emits the same
// `slots` contract the cart builder already understands.

// Photos tiled per physical sheet (for the preview mockup only) — derived from
// the print name. Sheet_count (how many sheets) is separate and drives `units`.
const photosPerSheet = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('card')) return 1; // a card is a single composed piece, not a tiled sheet
  if (n.includes('wallet')) return 8;
  if (n.includes('3x5')) return 4;
  if (n.includes('5x7')) return 2;
  return 1;
};

// Sheet mock aspect ratio per print type (Figma getSheetAspectRatio): a card is a
// single landscape piece; a sheet of 5x7s/wallets reads landscape; 3x5 slightly
// tall; 8x10/digital portrait.
const sheetAspect = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('card')) return '7 / 5';
  if (n.includes('wallet') || n.includes('5x7')) return '16 / 10';
  if (n.includes('3x5')) return '3 / 4';
  return '4 / 5';
};

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const s = {
  page: { background: t.pageBg, minHeight: '100vh', paddingBottom: '6rem' },
  header: { background: t.surface, borderBottom: `1px solid ${t.border}` },
  headerInner: {
    maxWidth: 1080, margin: '0 auto', padding: '0.9rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
  },
  back: {
    background: 'none',
    border: 'none',
    color: t.brand,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '0.95rem',
    padding: 0,
  },
  hTitle: { fontWeight: 700, color: t.ink, fontSize: '1.1rem' },
  hSub: { color: t.muted, fontSize: '0.85rem' },
  body: {
    maxWidth: 1080,
    margin: '0 auto',
    padding: '1.25rem',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))',
    gap: '1.5rem',
    alignItems: 'start',
  },
  card: {
    background: t.surface,
    borderRadius: t.radius,
    boxShadow: t.shadowSm,
    border: '1px solid rgba(0,0,0,.06)',
    overflow: 'hidden',
  },
  previewWrap: {
    padding: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  previewLabel: { fontWeight: 700, color: t.ink, marginBottom: '0.75rem' },
  sheet: {
    width: '100%',
    maxWidth: 360,
    aspectRatio: '4 / 5',
    borderRadius: 8,
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,.18)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    background: '#0b1b2b',
    display: 'grid',
    gap: 4,
    padding: 4,
  },
  tile: {
    width: '100%', height: '100%', objectFit: 'cover', borderRadius: 3, display: 'block',
  },
  // Default preview before a pose is chosen (Figma "Choose Pose"): a white sheet
  // with a centered + button and prompt.
  emptySheet: {
    width: '100%',
    maxWidth: 360,
    aspectRatio: '4 / 5',
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,.18)',
    background: '#fff',
    border: `1px solid ${t.border}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
  },
  emptyPlus: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: t.mutedSurface,
    color: t.muted,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.9rem',
    lineHeight: 1,
  },
  emptyText: { color: t.muted, fontWeight: 500 },
  // Sheet navigation, under the preview (Figma).
  sheetNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    borderTop: `1px solid ${t.border}`,
    padding: '0.85rem',
  },
  navBtn: {
    border: `1px solid ${t.border}`,
    background: t.surface,
    borderRadius: t.radiusSm,
    width: 34,
    height: 34,
    cursor: 'pointer',
    fontSize: '1rem',
    lineHeight: 1,
    color: t.body,
  },
  navBtnOff: { opacity: 0.4, cursor: 'not-allowed' },
  sheetTitle: {
    fontWeight: 600, color: t.ink, textAlign: 'center', fontSize: '0.9rem',
  },
  section: { padding: '1rem 1.25rem' },
  sectionTitle: {
    fontWeight: 700, color: t.ink, margin: '0 0 0.75rem', fontSize: '0.95rem',
  },
  strip: {
    display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.35rem',
  },
  poseBtn: {
    flex: '0 0 auto',
    border: '2px solid transparent',
    borderRadius: 8,
    padding: 0,
    background: 'none',
    cursor: 'pointer',
    position: 'relative',
  },
  poseBtnSel: { border: `2px solid ${t.brand}` },
  poseImg: {
    width: 84,
    height: 105,
    objectFit: 'cover',
    borderRadius: 6,
    display: 'block',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundColor: '#f3f4f6',
  },
  swatch: {
    flex: '0 0 auto',
    width: 72,
    height: 90,
    borderRadius: 8,
    border: '2px solid transparent',
    cursor: 'pointer',
    overflow: 'hidden',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.7rem',
    color: '#4b5563',
    background: t.mutedSurface,
    padding: 0,
  },
  swatchSel: { border: `2px solid ${t.brand}` },
  applyAll: {
    marginTop: '0.75rem',
    background: t.brandTint,
    color: t.brandHover,
    border: 'none',
    borderRadius: t.radiusSm,
    padding: '0.5rem 1rem',
    fontWeight: 700,
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  bar: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 25,
    background: t.surface,
    borderTop: `1px solid ${t.border}`,
    boxShadow: '0 -4px 16px rgba(0,0,0,.1)',
  },
  barInner: {
    maxWidth: 1080,
    margin: '0 auto',
    padding: '0.85rem 1.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '0.75rem 1rem',
  },
  barRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginLeft: 'auto',
  },
  addBtn: {
    background: t.brand,
    color: '#fff',
    border: 'none',
    borderRadius: t.radius,
    padding: '0.8rem 1.75rem',
    fontWeight: 700,
    fontSize: '1rem',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};

const PackageBuilder = ({
  variant, backgrounds, defaultBackgroundId, poses, backgroundUrl, templates,
  defaultTemplateId, templateRequired, studentName, adding, onConfirm, onBack,
}) => {
  // This choice's own pose order when it declares one, otherwise the gallery's (#5435).
  // Partial by design, exactly as the gallery treats it: declared numbers first in the
  // order given, everything else after by its existing place -- so a short list means
  // "show these first", never "hide the rest".
  const orderedPoses = useMemo(() => {
    const declared = variant.pose_order;
    if (!declared || !declared.length) return poses;

    const rank = new Map(declared.map((n, i) => [Number(n), i]));
    return [...poses].sort((a, b) => {
      const ra = rank.has(a.number) ? rank.get(a.number) : Infinity;
      const rb = rank.has(b.number) ? rank.get(b.number) : Infinity;
      return ra - rb || poses.indexOf(a) - poses.indexOf(b);
    });
  }, [poses, variant.pose_order]);

  const posesById = useMemo(() => Object.fromEntries(poses.map((p) => [p.id, p])), [poses]);
  const bgsById = useMemo(() => Object.fromEntries(backgrounds.map((b) => [b.id, b])), [backgrounds]);

  // Expand print slots into one customizable sheet per physical sheet.
  const units = useMemo(() => (variant.slots || []).flatMap((slot) => {
    const count = Math.max(slot.count || 1, 1);
    return Array.from({ length: count }, (_, i) => ({
      key: `${slot.image_type_id}:${i}`,
      imageTypeId: slot.image_type_id,
      label: count > 1 ? `${slot.name} #${i + 1}` : slot.name,
      photos: photosPerSheet(slot.name),
    }));
  }), [variant.slots]);

  // Sheets start with no pose (so the preview shows the Figma "Choose Pose" empty
  // state) but with the package's default background pre-selected, so there is no
  // "studio default" pseudo-option — every sheet has a real background from the set.
  const initialBg = (defaultBackgroundId && backgrounds.some((b) => b.id === defaultBackgroundId))
    ? defaultBackgroundId
    : (backgrounds[0]?.id || null);
  // Every sheet starts with no pose so the preview shows the Figma "Choose pose"
  // placeholder; the shopper picks a pose per sheet (or "Apply to all").
  const [sel, setSel] = useState(
    () => Object.fromEntries(units.map((u) => [u.key, { poseId: null, bgId: initialBg }])),
  );
  const [current, setCurrent] = useState(0);
  // Display style for this product (null = plain photo), recorded on the order line
  // at confirm. It is RESOLVED from the setup, never chosen here: the look belongs to
  // the package the way its prints do, and a shopper toggling it off was choosing
  // between two products rather than configuring one. The picker that did that is
  // gone -- what it opened on is simply what the package is now.
  //
  // A style-led package (a graduation card) names its look as the default; one that
  // only requires A look takes the first offered; anything else is a plain photo.
  const templateId = defaultTemplateId
    || (templateRequired ? (templates[0] && templates[0].id) || null : null);
  const template = templates.find((tpl) => tpl.id === templateId) || null;

  // Auto-styled (favorites) flow: when the poses carry their OWN per-pose look
  // (pose.composite, derived server-side from the pose number), the look travels with
  // the pose and the shopper picks a pose that is already styled. This is still the
  // one thing that overrides `template` above, and the branch below at confirm is why
  // it has to stay: the order line takes the chosen pose's look, not the product's.
  const autoStyled = useMemo(() => poses.some((p) => p.composite && p.composite.template), [poses]);
  const compositeFor = (poseId) => {
    const p = posesById[poseId];
    return autoStyled && p && p.composite && p.composite.template ? p.composite : null;
  };

  // A buy option with nothing to configure. It gets here because selectVariant
  // opens the builder for any STYLED variant, and a variant with no legacy option
  // (so no image types) has no sheets -- product 67's "Option 2729" is exactly
  // that, priced and offering styles with nothing linked behind it. Reading
  // units[0].key threw "Cannot read properties of undefined", which white-screens
  // the whole gallery rather than failing the one option. The cart refuses such a
  // variant anyway (CartBuilder#add_package -> "Please choose a photo for your
  // order."), so there is nothing to build -- say so and let them back out.
  if (!units.length) {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <div style={s.headerInner}>
            <button type="button" style={s.back} onClick={onBack}>←</button>
            <div>
              <div style={s.hTitle}>{`Build your ${variant.name}`}</div>
              <div style={s.hSub}>This option has no prints to set up.</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const unit = units[current] || units[0];
  const cur = sel[unit.key] || { poseId: null, bgId: initialBg };

  const setPose = (poseId) => setSel((m) => ({ ...m, [unit.key]: { ...m[unit.key], poseId } }));
  const setBg = (bgId) => setSel((m) => ({ ...m, [unit.key]: { ...m[unit.key], bgId } }));
  const applyAll = (field, value) => setSel(
    (m) => Object.fromEntries(Object.entries(m).map(([k, v]) => [k, { ...v, [field]: value }])),
  );

  // Extra-pose pricing (only when the package actually charges per extra pose).
  const chargesForPoses = (variant.additional_pose_charge || 0) > 0;
  const distinctPoses = new Set(units.map((u) => sel[u.key]?.poseId).filter(Boolean)).size;
  const extraPoses = chargesForPoses ? Math.max(0, distinctPoses - (variant.included_poses || 0)) : 0;
  const total = (variant.price || 0) + extraPoses * (variant.additional_pose_charge || 0);

  const previewBg = cur.bgId != null ? bgsById[cur.bgId]?.image_url : backgroundUrl;
  const previewPose = posesById[cur.poseId];
  // The look shown in the preview: for an auto-styled pose it's the pose's OWN
  // composite (spec + raw source url); otherwise the picker's chosen template.
  const previewComposite = compositeFor(cur.poseId);
  const activeTemplate = autoStyled ? (previewComposite && previewComposite.template) : template;
  const activePhotoUrl = previewComposite ? previewComposite.source_url : previewPose?.watermark_url;
  let cols = 1;
  if (unit.photos >= 8) cols = 4; else if (unit.photos >= 2) cols = 2;
  const rows = Math.ceil(unit.photos / cols);
  // A composited look carries its OWN aspect (a memory-mate/collage is landscape).
  // Drive the sheet cells from it so a horizontal look fills a horizontal cell
  // (side by side) instead of letterboxing in a portrait cell -- matching how the
  // legacy flow lays out its composited sheets. Raw poses (no look) keep the
  // print-size default.
  const lookAspect = activeTemplate && activeTemplate.canvas_width && activeTemplate.canvas_height
    ? activeTemplate.canvas_width / activeTemplate.canvas_height
    : null;
  const lookLandscape = lookAspect != null && lookAspect > 1;
  const sheetAspectRatio = lookAspect ? `${lookAspect * cols} / ${rows}` : sheetAspect(unit.label);

  const extraCost = extraPoses * (variant.additional_pose_charge || 0);
  const extraNote = extraPoses > 0
    ? ` · +${money(extraCost)} for ${extraPoses} extra pose${extraPoses === 1 ? '' : 's'}`
    : '';

  // Every sheet needs a chosen pose before checkout. (Background always has the
  // studio default, so it never blocks.) Surface the first unfinished sheet.
  const unfinished = units.filter((u) => !sel[u.key]?.poseId);
  const ready = poses.length > 0 && unfinished.length === 0;
  // Guided sheet-by-sheet flow: the primary CTA advances ("Next sheet") once the
  // current sheet has a pose (+ background, which always defaults to studio); only
  // the last sheet shows "Add to cart". Arrows still free-navigate for review.
  const isLast = current === units.length - 1;
  const currentComplete = poses.length > 0 && !!cur.poseId;
  const goNext = () => setCurrent((c) => Math.min(units.length - 1, c + 1));

  const confirm = () => {
    if (!ready) {
      const idx = units.findIndex((u) => !sel[u.key]?.poseId);
      if (idx >= 0) setCurrent(idx); // jump to the sheet that still needs a pose
      return;
    }
    // Auto-styled: the look is the selected pose's own pre-selected style (derived
    // from its pose number); take the first chosen pose's look for the order line.
    // Otherwise use the picker's chosen template.
    const firstPoseId = units.map((u) => sel[u.key]?.poseId).find(Boolean);
    const chosenTemplateId = autoStyled
      ? (compositeFor(firstPoseId)?.template?.id ?? null)
      : templateId;
    onConfirm({
      slots: units.map((u) => ({
        image_type_id: u.imageTypeId,
        pose_id: sel[u.key]?.poseId,
        background_id: sel[u.key]?.bgId,
      })),
      templateId: chosenTemplateId,
    });
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.headerInner}>
          <button type="button" style={s.back} onClick={onBack}>←</button>
          <div>
            <div style={s.hTitle}>{`Build your ${variant.name}`}</div>
            <div style={s.hSub}>Choose a pose &amp; background for each print.</div>
          </div>
        </div>
      </div>

      <div style={s.body}>
        {/* Preview */}
        <div style={s.card}>
          <div style={s.previewWrap}>
            <div style={s.previewLabel}>Preview</div>
            {previewPose?.watermark_url ? (
              <div
                style={{
                  ...s.sheet,
                  maxWidth: lookLandscape ? 560 : s.sheet.maxWidth,
                  aspectRatio: sheetAspectRatio,
                  gridTemplateColumns: `repeat(${cols}, 1fr)`,
                  backgroundImage: previewBg ? `url(${previewBg})` : undefined,
                }}
              >
                {Array.from({ length: unit.photos }, (_, i) => (
                  activeTemplate
                    ? (
                      // eslint-disable-next-line react/no-array-index-key
                      <div key={i} style={s.tile}>
                        <TemplatePreview
                          template={activeTemplate}
                          photoUrl={activePhotoUrl}
                          backgroundUrl={previewBg}
                          studentName={studentName}
                          style={{ width: '100%', height: '100%' }}
                        />
                      </div>
                    )
                    // eslint-disable-next-line react/no-array-index-key
                    : <img key={i} src={previewPose.watermark_url} alt="" style={s.tile} />
                ))}
              </div>
            ) : (
              // Empty state before a pose is chosen (Figma "Choose Pose").
              <div style={{ ...s.emptySheet, aspectRatio: sheetAspectRatio }}>
                <span style={s.emptyPlus} aria-hidden="true">+</span>
                <span style={s.emptyText}>Choose pose</span>
              </div>
            )}
          </div>
          {/* Sheet navigation, under the preview (Figma). Arrows free-navigate so
              you can revisit/update any sheet. */}
          <div style={s.sheetNav}>
            <button
              type="button"
              style={current === 0 ? { ...s.navBtn, ...s.navBtnOff } : s.navBtn}
              disabled={current === 0}
              onClick={() => setCurrent((c) => Math.max(0, c - 1))}
              aria-label="Previous sheet"
            >
              ‹
            </button>
            <span style={s.sheetTitle}>{`${unit.label} · Sheet ${current + 1} of ${units.length}`}</span>
            <button
              type="button"
              style={current === units.length - 1 ? { ...s.navBtn, ...s.navBtnOff } : s.navBtn}
              disabled={current === units.length - 1}
              onClick={() => setCurrent((c) => Math.min(units.length - 1, c + 1))}
              aria-label="Next sheet"
            >
              ›
            </button>
          </div>
        </div>

        {/* Pose + background pickers */}
        <div>
          <div style={{ ...s.card, marginBottom: '1.25rem' }}>
            <div style={s.section}>
              <h3 style={s.sectionTitle}>Select a pose</h3>
              <div style={s.strip}>
                {orderedPoses.map((p) => {
                  const thumbComposite = compositeFor(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      style={cur.poseId === p.id ? { ...s.poseBtn, ...s.poseBtnSel } : s.poseBtn}
                      onClick={() => setPose(p.id)}
                      aria-label={`Pose ${p.number}`}
                    >
                      {thumbComposite ? (
                        <div
                          style={{
                            ...s.poseImg,
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <TemplatePreview
                            template={thumbComposite.template}
                            photoUrl={thumbComposite.source_url}
                            backgroundUrl={backgroundUrl}
                            studentName={studentName}
                          />
                        </div>
                      ) : (
                        <img
                          src={p.watermark_url}
                          alt=""
                          style={{ ...s.poseImg, backgroundImage: backgroundUrl ? `url(${backgroundUrl})` : undefined }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
              {units.length > 1 && (
                <button type="button" style={s.applyAll} onClick={() => applyAll('poseId', cur.poseId)}>
                  Apply pose to all sheets
                </button>
              )}
            </div>
          </div>

          {backgrounds.length > 0 && (
            <div style={s.card}>
              <div style={s.section}>
                <h3 style={s.sectionTitle}>Select a background</h3>
                <div style={s.strip}>
                  {backgrounds.map((bg) => (
                    <button
                      key={bg.id}
                      type="button"
                      style={{
                        ...(cur.bgId === bg.id ? { ...s.swatch, ...s.swatchSel } : s.swatch),
                        backgroundImage: bg.image_url ? `url(${bg.image_url})` : undefined,
                      }}
                      onClick={() => setBg(bg.id)}
                      aria-label={bg.label || 'Background'}
                    >
                      {bg.image_url ? '' : (bg.label || 'BG')}
                    </button>
                  ))}
                </div>
                {units.length > 1 && (
                  <button type="button" style={s.applyAll} onClick={() => applyAll('bgId', cur.bgId)}>
                    Apply background to all sheets
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={s.bar}>
        <div style={s.barInner}>
          <div style={s.barRight}>
            {isLast ? (
              <>
                <div style={{ color: ready ? t.muted : t.danger, fontSize: '0.85rem' }}>
                  {ready
                    ? `${units.length} sheet${units.length === 1 ? '' : 's'}${extraNote}`
                    : `Choose a pose for ${unfinished.length} more sheet${unfinished.length === 1 ? '' : 's'}`}
                </div>
                <button
                  type="button"
                  style={ready ? s.addBtn : { ...s.addBtn, opacity: 0.55, cursor: 'not-allowed' }}
                  disabled={adding || !ready}
                  onClick={confirm}
                >
                  {adding ? 'Adding…' : `Add to cart — ${money(total)}`}
                </button>
              </>
            ) : (
              <>
                {!currentComplete && (
                  <div style={{ color: t.danger, fontSize: '0.85rem' }}>Choose a pose to continue</div>
                )}
                <button
                  type="button"
                  style={currentComplete ? s.addBtn : { ...s.addBtn, opacity: 0.55, cursor: 'not-allowed' }}
                  disabled={!currentComplete}
                  onClick={goNext}
                >
                  Next sheet →
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

PackageBuilder.propTypes = {
  variant: PropTypes.shape({
    name: PropTypes.string,
    price: PropTypes.number,
    included_poses: PropTypes.number,
    // Pose numbers this choice shows first; absent means the gallery's order (#5435).
    pose_order: PropTypes.arrayOf(PropTypes.number),
    additional_pose_charge: PropTypes.number,
    slots: PropTypes.arrayOf(PropTypes.shape({
      image_type_id: PropTypes.number,
      name: PropTypes.string,
      count: PropTypes.number,
    })),
  }).isRequired,
  backgrounds: PropTypes.arrayOf(PropTypes.object),
  defaultBackgroundId: PropTypes.number,
  poses: PropTypes.arrayOf(PropTypes.object).isRequired,
  backgroundUrl: PropTypes.string,
  templates: PropTypes.arrayOf(PropTypes.object),
  defaultTemplateId: PropTypes.number,
  templateRequired: PropTypes.bool,
  studentName: PropTypes.string,
  adding: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
};

PackageBuilder.defaultProps = {
  backgrounds: [],
  defaultBackgroundId: null,
  backgroundUrl: null,
  templates: [],
  defaultTemplateId: null,
  templateRequired: false,
  studentName: null,
  adding: false,
};

export default PackageBuilder;
